import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { usersAPI, settingsAPI, restaurantsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Edit, Trash2, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'developer',
    restaurant_id: '',
    restaurant_ids: [],
    telegram_username: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, settingsRes, restaurantsRes] = await Promise.all([
        usersAPI.getAll(),
        settingsAPI.get(),
        restaurantsAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setSettings(settingsRes.data);
      setRestaurants(restaurantsRes.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setUserForm(f => ({ ...f, password }));
  };

  const handleOpenUserModal = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setUserForm({
        name: userToEdit.name,
        email: userToEdit.email,
        password: '',
        role: userToEdit.role,
        restaurant_id: userToEdit.restaurant_id || '',
        restaurant_ids: userToEdit.restaurant_ids || [],
        telegram_username: userToEdit.telegram_username || '',
      });
    } else {
      setEditingUser(null);
      setUserForm({
        name: '',
        email: '',
        password: '',
        role: 'developer',
        restaurant_id: '',
        restaurant_ids: [],
        telegram_username: '',
      });
      generatePassword();
    }
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email || (!editingUser && !userForm.password)) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      if (editingUser) {
        const updates = { ...userForm };
        if (!updates.password) delete updates.password;
        await usersAPI.update(editingUser.id, updates);
        toast.success('User updated');
      } else {
        await usersAPI.create(userForm);
        toast.success('User created');
      }
      setShowUserModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await usersAPI.delete(userId);
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  if (loading) {
    return (
      <DashboardLayout pageTitle="Settings">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Settings">
      <div className="space-y-6" data-testid="settings-page">
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="bg-white border border-[#E0E0E0] w-full overflow-x-auto flex-nowrap justify-start">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="ai_prompt">AI Prompt</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="api">API Keys</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
              <div className="p-4 border-b border-[#E0E0E0] flex justify-between items-center">
                <h3 className="font-semibold">User Management</h3>
                <Button
                  onClick={() => handleOpenUserModal()}
                  className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white"
                  data-testid="add-user-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Add User
                </Button>
              </div>
              
              {/* Desktop Table */}
              <div className="hidden md:block">
                <table className="w-full">
                  <thead className="bg-[#F5F5F5]">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Name</th>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Email</th>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Role</th>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Restaurant</th>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Status</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-t border-[#E0E0E0]">
                        <td className="p-3 font-medium">{u.name}</td>
                        <td className="p-3 text-sm text-[#666666]">{u.email}</td>
                        <td className="p-3">
                          <Badge variant="outline" className="capitalize">{u.role}</Badge>
                        </td>
                        <td className="p-3 text-sm text-[#666666]">
                          {restaurants.find(r => r.id === u.restaurant_id)?.name || '-'}
                        </td>
                        <td className="p-3">
                          <Badge className={u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {u.status || 'active'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenUserModal(u)}>
                              <Edit size={16} />
                            </Button>
                            {u.id !== user?.id && (
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u.id)} className="text-red-500">
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-[#E0E0E0]">
                {users.map(u => (
                  <div key={u.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{u.name}</p>
                        <p className="text-xs text-[#666666]">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenUserModal(u)}><Edit size={16} /></Button>
                        {u.id !== user?.id && (
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u.id)} className="text-red-500"><Trash2 size={16} /></Button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize" style={{fontSize:'11px'}}>{u.role}</Badge>
                      <Badge className={u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'} style={{fontSize:'11px'}}>{u.status || 'active'}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* General Settings Tab */}
          <TabsContent value="general" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Portal Name</Label>
                  <Input
                    value={settings.portal_name || ''}
                    onChange={(e) => setSettings(s => ({ ...s, portal_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Default Currency</Label>
                  <Select
                    value={settings.default_currency || 'USD'}
                    onValueChange={(v) => setSettings(s => ({ ...s, default_currency: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                      <SelectItem value="AED">AED (د.إ)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date Format</Label>
                  <Select
                    value={settings.date_format || 'YYYY-MM-DD'}
                    onValueChange={(v) => setSettings(s => ({ ...s, date_format: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Timezone</Label>
                  <Select
                    value={settings.timezone || 'UTC'}
                    onValueChange={(v) => setSettings(s => ({ ...s, timezone: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time</SelectItem>
                      <SelectItem value="Europe/London">London</SelectItem>
                      <SelectItem value="Asia/Dubai">Dubai</SelectItem>
                      <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button onClick={handleSaveSettings} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white">
                Save Settings
              </Button>
            </div>
          </TabsContent>

          {/* AI System Prompt Tab */}
          <TabsContent value="ai_prompt" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6 space-y-4">
              <div>
                <h3 className="font-semibold mb-2">AI Analysis System Prompt</h3>
                <p className="text-sm text-[#666666] mb-4">
                  This prompt is used by Claude AI when analyzing ideas from the Idea Bank. 
                  Customize it to match your business priorities and evaluation criteria.
                  Leave empty to use the default prompt.
                </p>
              </div>
              
              <div>
                <Label>System Prompt</Label>
                <textarea
                  value={settings.ai_system_prompt || ''}
                  onChange={(e) => setSettings(s => ({ ...s, ai_system_prompt: e.target.value }))}
                  className="w-full min-h-[300px] p-3 border border-[#E0E0E0] rounded-md text-sm font-mono bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#CFFF40] resize-y"
                  placeholder={`You are a product manager AI for Mayo Platform - restaurant management SaaS.\nAnalyze this feature idea and provide:\n1. Feasibility score (0-100)\n2. Business value score (0-100)\n3. Technical complexity score (0-100, 0=easy, 100=complex)\n4. Overall priority score (0-100)\n5. Recommendation (Accept/Review/Decline)\n6. Reasoning (2-3 sentences)\n\nIMPORTANT: Output ONLY valid JSON:\n{\n  "feasibility_score": <number>,\n  "business_value_score": <number>,\n  "technical_complexity_score": <number>,\n  "overall_priority_score": <number>,\n  "recommendation": "<Accept|Review|Decline>",\n  "reasoning": "<string>",\n  "estimated_dev_weeks": <number>,\n  "revenue_potential": "<High|Medium|Low>",\n  "affected_module": "<string>"\n}`}
                  data-testid="ai-system-prompt-input"
                />
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleSaveSettings} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="save-ai-prompt-btn">
                  Save Prompt
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setSettings(s => ({ ...s, ai_system_prompt: '' }))}
                >
                  Reset to Default
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6 space-y-4">
              <h3 className="font-semibold mb-4">Email Notifications</h3>
              
              <div className="space-y-3">
                {[
                  { key: 'new_idea', label: 'New idea submitted' },
                  { key: 'task_assigned', label: 'Task assigned to me' },
                  { key: 'task_completed', label: 'Task completed' },
                  { key: 'comment_added', label: 'New comment on my task' },
                  { key: 'restaurant_added', label: 'New restaurant added' },
                  { key: 'module_activated', label: 'Module activated for restaurant' },
                ].map(item => (
                  <div key={item.key} className="flex items-center gap-2">
                    <Checkbox
                      id={item.key}
                      checked={settings.notifications?.[item.key] || false}
                      onCheckedChange={(checked) => setSettings(s => ({
                        ...s,
                        notifications: { ...s.notifications, [item.key]: checked }
                      }))}
                    />
                    <Label htmlFor={item.key} className="cursor-pointer">{item.label}</Label>
                  </div>
                ))}
              </div>
              
              <Button onClick={handleSaveSettings} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white mt-4">
                Save Notifications
              </Button>
            </div>
          </TabsContent>

          {/* API Keys Tab */}
          <TabsContent value="api" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6 space-y-4">
              <div>
                <Label>Claude API Key (for AI Analysis)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="password"
                    value="••••••••••••••••••••"
                    readOnly
                    className="flex-1"
                  />
                  <Button variant="outline">Test Connection</Button>
                </div>
                <p className="text-xs text-[#666666] mt-1">Using Emergent LLM Key</p>
              </div>
              
              <div>
                <Label>Object Storage</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="password"
                    value="••••••••••••••••••••"
                    readOnly
                    className="flex-1"
                  />
                  <Button variant="outline">Test Connection</Button>
                </div>
                <p className="text-xs text-[#666666] mt-1">Using Emergent Object Storage</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm(f => ({ ...f, email: e.target.value }))}
                placeholder="email@example.com"
              />
            </div>
            
            <div>
              <Label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={userForm.password}
                    onChange={(e) => setUserForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#666666]"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <Button variant="outline" onClick={generatePassword}>
                  <RefreshCw size={16} />
                </Button>
              </div>
            </div>
            
            <div>
              <Label>Role *</Label>
              <Select
                value={userForm.role}
                onValueChange={(v) => setUserForm(f => ({ ...f, role: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                  <SelectItem value="developer">Developer</SelectItem>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Telegram Username <span className="text-[#999999] font-normal">(for task notifications)</span></Label>
              <Input
                value={userForm.telegram_username}
                onChange={(e) => setUserForm(f => ({ ...f, telegram_username: e.target.value }))}
                placeholder="@username"
                data-testid="user-telegram-username"
              />
            </div>
            
            {userForm.role === 'restaurant' && (
              <div>
                <Label>Restaurant</Label>
                <Select
                  value={userForm.restaurant_id}
                  onValueChange={(v) => setUserForm(f => ({ ...f, restaurant_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select restaurant" />
                  </SelectTrigger>
                  <SelectContent>
                    {restaurants.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {userForm.role === 'partner' && (
              <div>
                <Label>Linked Restaurants</Label>
                <div className="border border-[#E0E0E0] rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                  {restaurants.map(r => (
                    <div key={r.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`partner-restaurant-${r.id}`}
                        checked={userForm.restaurant_ids?.includes(r.id)}
                        onCheckedChange={(checked) => {
                          setUserForm(f => ({
                            ...f,
                            restaurant_ids: checked
                              ? [...(f.restaurant_ids || []), r.id]
                              : (f.restaurant_ids || []).filter(id => id !== r.id)
                          }));
                        }}
                      />
                      <label htmlFor={`partner-restaurant-${r.id}`} className="text-sm cursor-pointer">
                        {r.name}
                      </label>
                    </div>
                  ))}
                  {restaurants.length === 0 && (
                    <p className="text-sm text-[#999999]">No restaurants available</p>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserModal(false)}>Cancel</Button>
            <Button
              onClick={handleSaveUser}
              className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white"
            >
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
